import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Eye, Calendar, User, Tag, Send, MessageSquare } from 'lucide-react';

export default function NewsDetail() {
  const { id } = useParams();
  const { t } = useLanguage();
  const [article, setArticle] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState({ content: '', author_name: '', author_email: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/articles/${id}`),
      api.get(`/articles/${id}/comments`),
    ]).then(([artRes, cmtRes]) => {
      setArticle(artRes.data);
      setComments(cmtRes.data || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  const submitComment = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/articles/${id}/comments`, comment);
      toast.success('Comentário submetido para moderação!');
      setComment({ content: '', author_name: '', author_email: '' });
    } catch { toast.error('Erro ao submeter comentário'); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <div className="animate-pulse space-y-4">
        <div className="h-64 bg-gray-200 rounded-xl" />
        <div className="h-8 bg-gray-200 rounded w-3/4" />
        <div className="space-y-2">{[...Array(5)].map(i => <div key={i} className="h-4 bg-gray-100 rounded" />)}</div>
      </div>
    </div>
  );

  if (!article) return (
    <div className="text-center py-20 text-gray-400">
      <p>Artigo não encontrado.</p>
      <Link to="/noticias" className="text-[#CC0000] hover:underline mt-2 block">Voltar às notícias</Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Image */}
      {article.featured_image && (
        <div className="relative h-72 md:h-96 overflow-hidden">
          <img src={article.featured_image} alt={article.title} className="w-full h-full object-cover" />
          <div className="hero-gradient absolute inset-0" />
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/noticias" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#CC0000] transition-colors mb-6">
          <ArrowLeft size={14} /> Voltar às notícias
        </Link>

        {/* Article Header */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2 mb-4">
            {article.category_name && <span className="badge badge-red">{article.category_name}</span>}
            {article.tags?.map(tag => (
              <span key={tag} className="badge badge-gray"><Tag size={9} className="mr-1" />{tag}</span>
            ))}
          </div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 leading-tight">{article.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 pb-6 border-b border-gray-200">
            <div className="flex items-center gap-1"><User size={14} /> {article.author_name}</div>
            {article.published_at && (
              <div className="flex items-center gap-1">
                <Calendar size={14} />
                {new Date(article.published_at).toLocaleDateString('pt', { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
            )}
            <div className="flex items-center gap-1"><Eye size={14} /> {article.view_count} visualizações</div>
          </div>
        </div>

        {/* Article Content */}
        <div className="prose max-w-none mb-12"
          dangerouslySetInnerHTML={{ __html: article.content }}
          data-testid="article-content"
        />

        {/* Comments */}
        <div className="border-t border-gray-200 pt-8" data-testid="comments-section">
          <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
            <MessageSquare size={20} className="text-[#CC0000]" />
            Comentários ({comments.length})
          </h2>

          {comments.length > 0 && (
            <div className="space-y-4 mb-8">
              {comments.map(c => (
                <div key={c.id} className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-full bg-[#CC0000] text-white flex items-center justify-center text-xs font-bold">
                      {c.author_name[0].toUpperCase()}
                    </div>
                    <span className="font-medium text-sm">{c.author_name}</span>
                    <span className="text-xs text-gray-400">{new Date(c.created_at).toLocaleDateString('pt')}</span>
                  </div>
                  <p className="text-sm text-gray-700">{c.content}</p>
                </div>
              ))}
            </div>
          )}

          {/* Comment Form */}
          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="font-semibold mb-4 text-sm">Deixe um comentário</h3>
            <form onSubmit={submitComment} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <input type="text" value={comment.author_name} onChange={e => setComment(c => ({...c, author_name: e.target.value}))}
                    required placeholder="Seu nome" data-testid="comment-name"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none bg-white" />
                </div>
                <div>
                  <input type="email" value={comment.author_email} onChange={e => setComment(c => ({...c, author_email: e.target.value}))}
                    required placeholder="Seu email" data-testid="comment-email"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none bg-white" />
                </div>
              </div>
              <textarea value={comment.content} onChange={e => setComment(c => ({...c, content: e.target.value}))}
                required placeholder="Escreva o seu comentário..." rows={3} data-testid="comment-content"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none bg-white resize-none" />
              <p className="text-xs text-gray-400">Os comentários estão sujeitos a moderação antes de serem publicados.</p>
              <button type="submit" disabled={submitting} data-testid="comment-submit"
                className="flex items-center gap-2 px-5 py-2.5 bg-[#CC0000] text-white text-sm font-medium rounded-md hover:bg-[#A30000] disabled:opacity-60 transition-colors">
                <Send size={14} /> {submitting ? 'Enviando...' : 'Enviar Comentário'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
